// When the terms or the privacy policy in force change (`GET /v1/meta`'s `brand.legal`), a signed-in player is asked
// to agree to the new version (F-CMP-06): a banner above every page, with the documents linked, until they agree
// (`POST /v1/me/consents`, a new row, the old one kept). "Later" hides it for this visit only.

import { useTranslation } from '@clavian/i18n/react';
import { Button, useToast } from '@clavian/ui-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { queryKeys, useBrandLegal, useConsents } from '../api/queries';
import { useProblem } from '../api/use-problem';

export function ConsentBanner(): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const queryClient = useQueryClient();
  const problem = useProblem();
  const { show } = useToast();
  const { legal } = useBrandLegal();
  const consents = useConsents();
  const [later, setLater] = useState(false);

  const accept = useMutation({
    mutationFn: (body: { terms?: string; privacy?: string }) => api.acceptConsents({ body }),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.consents, saved);
      show({ title: t('account.legal.agreed'), tone: 'ok' });
    },
    onError: (error) => show({ title: problem.text(error), tone: 'danger' }),
  });

  if (legal === undefined || consents.data === undefined || later) return null;
  const termsOutdated = consents.data.terms?.version !== legal.terms.version;
  const privacyOutdated = consents.data.privacy?.version !== legal.privacy.version;
  if (!termsOutdated && !privacyOutdated) return null;

  const documents =
    termsOutdated && privacyOutdated ? t('account.legal.updatedBoth') : termsOutdated ? t('account.legal.terms') : t('account.legal.privacy');

  return (
    <section className="pw-banner" aria-labelledby="pw-consent-title">
      <div className="pw-banner__text">
        <h2 id="pw-consent-title" className="pw-banner__title">
          {t('account.legal.updatedTitle')}
        </h2>
        <p>{t('account.legal.updatedBody', { documents })}</p>
        <p className="pw-banner__links">
          {termsOutdated ? (
            <a href={legal.terms.url} target="_blank" rel="noreferrer">
              {t('account.legal.read', { document: t('account.legal.terms') })}
            </a>
          ) : null}
          {privacyOutdated ? (
            <a href={legal.privacy.url} target="_blank" rel="noreferrer">
              {t('account.legal.read', { document: t('account.legal.privacy') })}
            </a>
          ) : null}
        </p>
      </div>
      <div className="pw-banner__actions">
        <Button
          variant="primary"
          busy={accept.isPending}
          onClick={() =>
            accept.mutate({
              ...(termsOutdated ? { terms: legal.terms.version } : {}),
              ...(privacyOutdated ? { privacy: legal.privacy.version } : {}),
            })
          }
        >
          {t('account.legal.agree')}
        </Button>
        <Button variant="ghost" onClick={() => setLater(true)}>
          {t('account.legal.later')}
        </Button>
      </div>
    </section>
  );
}
